/**
 * Feature 022 — client-side notes search engine.
 *
 * Parses a Precision Notes chapter's authored HTML fragment (the `b-sidebyside` example blocks
 * produced by the BridgeBookHtmlConverter) into hand-bearing example units, and matches a
 * user-built bidding sequence against each example's auction using the opening-bid-anchored prefix
 * rule (spec FR-024): after skipping any passes that precede the auction's opening bid, the auction
 * must begin with the entered run of calls.
 *
 * Bid parsing/normalisation reuses `utils/bidValidation.ts` so the picker and the matcher share one
 * source of truth (research.md D1/D3).
 */

import { parseBid, getBidValue } from './bidValidation';
import { decorateChapterDoc, paginateDocument, sectionAnchorId } from './chapterPagination';

export type CallKind = 'bid' | 'pass' | 'double' | 'redouble' | 'unknown';

export interface NormalizedCall {
  /** Original token as authored (e.g. `1d`, `dbl`, `p`, `2s*`) or as built (`1D`, `Pass`). */
  raw: string;
  kind: CallKind;
  /** Comparison value for contract bids (level*5 + suitIndex); null otherwise. */
  value: number | null;
}

export interface NoteExample {
  chapterSlug: string;
  chapterOrder: number;
  chapterTitle: string;
  /** The authored context line (nearest preceding `p.b-handintro`), shown verbatim. */
  contextLine: string;
  handHtml: string;
  auctionHtml: string;
  questionHtml: string | null;
  commentsHtml: string[];
  /** The flattened, normalised auction call list used for matching. */
  calls: NormalizedCall[];
  /** 1-based reader page this example falls on, from the shared `chapterPagination` split. */
  pageNumber: number;
  /** Text of the nearest preceding heading, or '' when the example sits before any heading. */
  sectionTitle: string;
  /** Anchor id of that heading (`b-sec-N`), or '' when there is no preceding heading. */
  sectionAnchor: string;
  /**
   * 1-based page carrying the section HEADING, which is often an earlier page than `pageNumber` —
   * a section commonly runs across several pages. A section link must target this page or the
   * anchor would not be on the rendered page and the jump would silently do nothing.
   */
  sectionPage: number;
}

export interface ChapterSearchIndex {
  slug: string;
  order: number;
  title: string;
  examples: NoteExample[];
}

export interface ChapterResultGroup {
  slug: string;
  order: number;
  title: string;
  examples: NoteExample[];
}

/** Normalise a single call token (searched or stored) into a comparable form. */
export function normalizeCall(raw: string): NormalizedCall {
  // Strip trailing BBO alert markers (e.g. `2s*`) before parsing so they don't defeat a match.
  const cleaned = raw.replace(/\*+$/, '').trim();
  const parsed = parseBid(cleaned);
  switch (parsed.type) {
    case 'bid':
      return parsed.level !== undefined && parsed.suit !== undefined
        ? { raw, kind: 'bid', value: getBidValue(parsed.level, parsed.suit) }
        : { raw, kind: 'unknown', value: null };
    case 'pass':
      return { raw, kind: 'pass', value: null };
    case 'double':
      return { raw, kind: 'double', value: null };
    case 'redouble':
      return { raw, kind: 'redouble', value: null };
    default:
      // 'placeholder' (?) and 'unknown' are non-calls for matching purposes.
      return { raw, kind: 'unknown', value: null };
  }
}

/** Two calls are equal when their kind matches (and, for contract bids, their value). */
function callsEqual(a: NormalizedCall, b: NormalizedCall): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'bid') return a.value === b.value;
  return true;
}

/**
 * Flatten a `table.b-auction-grid` element into an ordered call list. Seat-label header cells are
 * wrapped in `<strong>` and skipped; a `?` (the "what do you bid" marker) terminates the auction.
 */
export function extractAuctionCalls(grid: Element): NormalizedCall[] {
  const calls: NormalizedCall[] = [];
  const cells = grid.querySelectorAll('td, th');
  for (const cell of Array.from(cells)) {
    // Seat labels (Op / P / player names) are bold headers — not calls.
    if (cell.querySelector('strong')) continue;
    const text = (cell.textContent ?? '').trim();
    if (text === '') continue;
    if (text === '?') break; // user's turn marker ends the shown auction
    calls.push(normalizeCall(text));
  }
  return calls;
}

/** The text of the nearest preceding `p.b-handintro` before an element (the example's context line). */
function findContextLine(el: Element): string {
  let node: Element | null = el.previousElementSibling;
  while (node) {
    if (node.matches?.('p.b-handintro')) return (node.textContent ?? '').trim();
    // Stop searching once we cross into an earlier example block.
    if (node.matches?.('table.b-sidebyside')) break;
    node = node.previousElementSibling;
  }
  return '';
}

/**
 * Map every top-level element to the 1-based reader page it lands on, using the SAME split the
 * reader uses — a cited page number is only useful if `ChapterPage` agrees with it.
 */
function buildPageMap(doc: Document): Map<Element, number> {
  const pageOf = new Map<Element, number>();
  paginateDocument(doc).forEach((elements, i) => {
    for (const el of elements) pageOf.set(el, i + 1);
  });
  return pageOf;
}

/**
 * The nearest preceding heading of an element. Anchors are keyed by the heading's document-order
 * index, matching the ids `chapterPagination` stamps at render time. `page` is the heading's own
 * page, which may precede the element's.
 */
function findSection(
  el: Element,
  headingIndexOf: Map<Element, number>,
  pageOf: Map<Element, number>,
): { title: string; anchor: string; page: number } {
  let node: Element | null = el.previousElementSibling;
  while (node) {
    const index = headingIndexOf.get(node);
    if (index !== undefined) {
      return {
        title: (node.textContent ?? '').trim(),
        anchor: sectionAnchorId(index),
        page: pageOf.get(node) ?? 1,
      };
    }
    node = node.previousElementSibling;
  }
  return { title: '', anchor: '', page: 1 };
}

/**
 * Parse one chapter's HTML fragment into its hand-bearing example blocks. A result unit is a
 * `table.b-sidebyside` (hand ↔ auction/Q/comments) with a `b-auction-grid`; standalone auctions with
 * no hand are excluded (spec FR-026). Each example also records where it lives in the reader (page
 * number + section) so a result can link back to it.
 */
export function parseChapterExamples(
  html: string,
  chapter: { slug: string; order: number; title: string },
): NoteExample[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Same decoration the reader applies, so a result's auction snippet renders with the reader's
  // column headers — `SearchResults` shares `learn-chapter.css`, and `auctionHtml` below is taken
  // from `grid.outerHTML`, so the classes must be on the grid before that snapshot is captured.
  decorateChapterDoc(doc);
  const blocks = doc.querySelectorAll('table.b-sidebyside');
  const examples: NoteExample[] = [];

  const pageOf = buildPageMap(doc);
  const headingIndexOf = new Map<Element, number>();
  Array.from(doc.querySelectorAll('h1, h2, h3, h4')).forEach((h, i) => headingIndexOf.set(h, i));

  for (const block of Array.from(blocks)) {
    const grid = block.querySelector('table.b-auction-grid');
    const handCell = block.querySelector('td');
    if (!grid || !handCell) continue; // not a hand-bearing example → skip

    const comments = Array.from(
      block.querySelectorAll('p.b-celljeff, p.b-celleric, p.b-cellanswer'),
    ).map((c) => c.innerHTML);

    const section = findSection(block, headingIndexOf, pageOf);

    examples.push({
      chapterSlug: chapter.slug,
      chapterOrder: chapter.order,
      chapterTitle: chapter.title,
      contextLine: findContextLine(block),
      handHtml: handCell.innerHTML,
      auctionHtml: grid.outerHTML,
      questionHtml: block.querySelector('p.b-cellquestion')?.innerHTML ?? null,
      commentsHtml: comments,
      calls: extractAuctionCalls(grid),
      pageNumber: pageOf.get(block) ?? 1,
      sectionTitle: section.title,
      sectionAnchor: section.anchor,
      sectionPage: section.page,
    });
  }

  return examples;
}

/** Build a chapter search index from its fetched HTML. */
export function buildChapterIndex(
  html: string,
  chapter: { slug: string; order: number; title: string },
): ChapterSearchIndex {
  return { ...chapter, examples: parseChapterExamples(html, chapter) };
}

/** Does an example's auction begin with the search sequence, anchored at the opening bid? */
export function exampleMatches(example: NoteExample, sequence: NormalizedCall[]): boolean {
  if (sequence.length === 0) return false;

  // Skip leading passes that precede the opening bid, then require a prefix match.
  let start = 0;
  while (start < example.calls.length && example.calls[start]!.kind === 'pass') start++;
  const anchored = example.calls.slice(start);
  if (anchored.length < sequence.length) return false;

  for (let i = 0; i < sequence.length; i++) {
    if (!callsEqual(anchored[i]!, sequence[i]!)) return false;
  }
  return true;
}

/**
 * Search every chapter index for examples whose auction begins with the built sequence, returning
 * chapter-grouped results in chapter order. `sequence` is the list of built call tokens
 * (e.g. `['1D','Pass','1H']`); empty input yields no groups.
 */
export function searchNotes(
  indexes: ChapterSearchIndex[],
  sequence: string[],
): ChapterResultGroup[] {
  const normalized = sequence.map(normalizeCall);
  if (normalized.length === 0) return [];

  const groups: ChapterResultGroup[] = [];
  for (const idx of [...indexes].sort((a, b) => a.order - b.order)) {
    const matches = idx.examples.filter((ex) => exampleMatches(ex, normalized));
    if (matches.length > 0) {
      groups.push({ slug: idx.slug, order: idx.order, title: idx.title, examples: matches });
    }
  }
  return groups;
}
